import * as moment from 'moment';
import { Component, OnInit, ViewChild } from '@angular/core';
import { EventService } from 'src/app/services/event/event.service';
import { EventType } from 'src/app/models/eventType.model';
import { Event } from 'src/app/models/event.model';
import { LoadingService } from 'src/app/services/loading/loading.service';
import { IonContent, ModalController } from '@ionic/angular';
import { TranslateService } from '@ngx-translate/core';
import { PortalService } from 'src/app/services/portal/portal.service';
import { Portal } from 'src/app/models/portal.model';
import { GroupService } from 'src/app/services/group/group.service';
import { Group } from 'src/app/models/group.model';
import constants from 'src/app/constants';

@Component({
  selector: 'app-create-event',
  templateUrl: './create-event-modal.page.html',
  styleUrls: ['./create-event-modal.page.scss'],
})
export class CreateEventModalPage implements OnInit {
  @ViewChild(IonContent, { static: false }) private content: IonContent;
  edit: boolean = false;
  errors: string[] = [];
  unsavedChanges: boolean = false;

  startDate: string = new Date().toISOString();
  endDate: string = new Date().toISOString();

  fileName: string;
  imageFile: string;

  event: Event = new Event();

  eventTypes: EventType[] = [];

  reminderIntervals: any[] = [];
  selectedReminder: number;

  portalId: number;
  portal: Portal;
  groups: Group[] = [];
  visibleGroups: Group[] = [];

  selectedEventType: number;
  allDay: boolean = false;
  visibility: string = "personal";

  rules: any = {}

  constructor(
    public translate: TranslateService,
    private eventService: EventService,
    private loadingService: LoadingService,
    private modalController: ModalController,
    private portalService: PortalService,
    private groupService: GroupService
  ) {
    this.translate.setDefaultLang("en");
    this.translate.use(this.translate.getBrowserLang() || "en");
  }

  ngOnInit() {
    this.reminderIntervals = [
      { minutes: 0, value: this.translate.instant('DATE.none'), showAllDay: true},
      { minutes: 15, value: this.translate.instant('DATE.minutes', { minutes: 15 }), showAllDay: false},
      { minutes: 30, value: this.translate.instant('DATE.minutes', { minutes: 30 }), showAllDay: false},
      { minutes: 45, value: this.translate.instant('DATE.minutes', { minutes: 45 }), showAllDay: false},
      { minutes: constants.MINUTES_IN_HOUR * 1, value: this.translate.instant('DATE.hour'), showAllDay: false},
      { minutes: constants.MINUTES_IN_HOUR * 2, value: this.translate.instant('DATE.hours', { hours: 2 }), showAllDay: false},
      { minutes: constants.MINUTES_IN_HOUR * 3, value: this.translate.instant('DATE.hours', { hours: 3 }), showAllDay: false},
      { minutes: constants.MINUTES_IN_HOUR * 6, value: this.translate.instant('DATE.hours', { hours: 6 }), showAllDay: false},
      { minutes: constants.MINUTES_IN_HOUR * 12, value: this.translate.instant('DATE.hours', { hours: 12 }), showAllDay: false},
      { minutes: constants.MINUTES_IN_DAY * 1, value: this.translate.instant('DATE.day'), showAllDay: true},
      { minutes: constants.MINUTES_IN_DAY * 2, value: this.translate.instant('DATE.days', { days: 2 }), showAllDay: true},
      { minutes: constants.MINUTES_IN_DAY * 3, value: this.translate.instant('DATE.days', { days: 3 }), showAllDay: true},
      { minutes: constants.MINUTES_IN_WEEK * 1, value: this.translate.instant('DATE.week'), showAllDay: true},
      { minutes: constants.MINUTES_IN_WEEK * 2, value: this.translate.instant('DATE.weeks', { weeks: 2 }), showAllDay: true},
    ]

    this.selectedReminder = 0;

    if (this.event.isPersonal) {
      this.visibility = "personal";
    } else {
      this.visibility = "portal";
    }

    if (this.event.momentEventDate && this.event.momentReminderTime) {
      // Calculate the difference in minutes between startDate and reminderTime to get the reminder interval
      let duration = moment.duration(this.event.momentEventDate.diff(this.event.momentReminderTime));
      this.selectedReminder = duration.asMinutes();
    }

    this.eventService.getEventTypes().subscribe(eventTypes => {
      this.eventTypes = eventTypes;

      if (this.event.eventTypeID) {
        this.selectedEventType = this.event.eventTypeID;
      }
    });

    this.portalService.getPortalByPortalID(this.portalId).subscribe(portal => {
      this.portal = portal;

      this.portalService.getPortalRules(this.portal.id).subscribe(rules => {
        this.rules = rules;
        if (!rules.create_portal_event) {
          this.visibility = "personal";
          this.event.isPersonal = true;
        }
      })

      this.groupService.getUserGroupsByPortalID(portal.id, true).subscribe(groups => {
        this.groups = groups;
        if (this.event.groups.length > 0) {
          this.groups.forEach(group => {
            // find if the group is already linked to the event
            if (this.event.groups.findIndex(eventGroup => eventGroup.id == group.id) >= 0) {
              group.groupSelected = true;
            }
          });
        }

      });
    });

  }

  onChangeStartDate() {
    this.markDirty();
    if (this.startDate > this.endDate) {
      this.endDate = this.startDate;
    }
  }

  onChangeEndDate() {
    this.markDirty();
    if (this.endDate < this.startDate) {
      this.startDate = this.endDate;
    }
  }

  onChangeStartTime() {
    this.markDirty();
    if (this.startDate > this.endDate) {
      this.endDate = this.startDate;
    }
  }

  onChangeEndTime() {
    this.markDirty();
    if (this.endDate < this.startDate) {
      this.startDate = this.endDate;
    }
  }

  createEvent() {
    this.errors = [];
    if(this.event.eventName && this. event.eventName.length > 0) {
      if(this.selectedEventType) {

        let startTime: string = null;
        let endTime: string = null;
        if (this.event.allDay) {
          startTime = moment.utc(this.startDate).format('YYYY-MM-DD');
          endTime = moment.utc(this.endDate).format('YYYY-MM-DD');
        } else {
          startTime = moment.utc(this.startDate).format('YYYY-MM-DD HH:mm');
          endTime = moment.utc(this.endDate).format('YYYY-MM-DD HH:mm');
        }

        let reminderTime: string = null;
        if (this.selectedReminder > 0) {
          reminderTime = moment.utc(startTime).subtract(this.selectedReminder, 'minutes').format('YYYY-MM-DD HH:mm');
        }

        this.loadingService.presentLoading(this.translate.instant("EVENT.addingEvent"));
        this.eventService.createEvent(
          this.event,
          startTime,
          endTime,
          this.selectedEventType,
          reminderTime,
          this.portal.id,
          this.groups)
        .subscribe(newEvent => {
          this.loadingService.dismiss();
          this.modalController.dismiss(newEvent);
        });
      } else {
        this.content.scrollToTop();
        this.errors.push(this.translate.instant("ERROR.selectEventType"));
      }
    } else {
      this.content.scrollToTop();
      this.errors.push(this.translate.instant("ERROR.enterEventName"));
    }
  }

  editEvent() {
    this.errors = [];
    if(this.event.eventName && this. event.eventName.length > 0) {
      if(this.selectedEventType) {
        let startTime: string = null;
        let endTime: string = null;
        if (this.event.allDay) {
          startTime = moment.utc(this.startDate).format('YYYY-MM-DD');
          endTime = moment.utc(this.endDate).format('YYYY-MM-DD');
        } else {
          startTime = moment.utc(this.startDate).format('YYYY-MM-DD HH:mm');
          endTime = moment.utc(this.endDate).format('YYYY-MM-DD HH:mm');
        }


        let reminderTime: string = null;
        if (this.selectedReminder > 0) {
          reminderTime = moment.utc(startTime).subtract(this.selectedReminder, 'minutes').format('YYYY-MM-DD HH:mm');
        }

        this.loadingService.presentLoading(this.translate.instant("EVENT.updatingEvent"));
        this.eventService.updateEvent(
          this.event,
          startTime,
          endTime,
          this.selectedEventType,
          reminderTime,
          this.portal.id,
          this.groups)
        .subscribe(newEvent => {
          this.loadingService.dismiss();
          this.modalController.dismiss(newEvent);
        });
      } else {
        this.content.scrollToTop();
        this.errors.push(this.translate.instant("ERROR.selectEventType"));
      }
    } else {
      this.content.scrollToTop();
      this.errors.push(this.translate.instant("ERROR.enterEventName"));
    }
  }

  allDayChanged() {
    this.markDirty();

    // if the event is changed to all day and the reminder is not available for all day events, set reminder back to 0
    if (this.event.allDay) {
      let selectedInterval = this.reminderIntervals.find((interval) => interval.minutes == this.selectedReminder);
      if (!selectedInterval.showAllDay) {
        this.selectedReminder = 0;
      }
    }
  }

  onChangeVisibilty(event) {
    this.event.isPersonal = event.detail.value == "personal"
  }

  markDirty() {
    this.unsavedChanges = true;
  }
}
